// frontend/src/pages/PublicReportDetails.js
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../utils/axiosConfig'; // 已設定 CSRF 的 axios
import API_URL from '../config';
import { useTranslation } from 'react-i18next';
import { translateIncidentType } from '../utils/translateIncidentType';

function PublicReportDetails() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 取得公開案件資料（後端需提供 /api/public_report_details/:id 路由）
  useEffect(() => {
    axios.get(`${API_URL}/api/public_report_details/${id}`)
      .then((res) => {
        setReport(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }, [id]);

  // 載入中或錯誤處理
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-700 dark:text-gray-300">
        {t('loading')}
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-500">
        {t('errorFetchingReport')}
      </div>
    );
  }

  // 若案件不是公開（public），則不允許顯示
  if (report.reportType !== 'public') {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-500">
        {t('reportNotPublic')}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Hero Banner */}
      <div
        className="w-full h-64 bg-cover bg-center flex items-center justify-center"
        style={{ backgroundImage: "url('/images/myReportsBanner.png')" }}  // 請自行提供適合的背景圖片
      >
        <div>
          <h1 className="text-3xl font-bold text-white">{t('reportDetails')}</h1>
        </div>
      </div>
        
      {/* 主要內容區 */}
      <div className="max-w-5xl mx-auto py-8 px-4">
        {/* 案件基本資訊 */}
       <div className="bg-black bg-opacity-50 p-4 rounded">
          <p className="mt-2 text-white">{t('reportId')}: {report._id}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('basicInformation')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-gray-700 dark:text-gray-300">
                <strong>{t('incidentType')}: </strong>
                {report.incidentType === 'other'
                  ? report.otherIncidentType
                  : translateIncidentType(report.incidentType, t)}
              </p>
              <p className="text-gray-700 dark:text-gray-300 mt-2">
                <strong>{t('reportType')}: </strong>
                {t(report.reportType, { defaultValue: report.reportType })}
              </p>
              <p className="text-gray-700 dark:text-gray-300 mt-2">
                <strong>{t('date')}: </strong>
                {new Date(report.createdAt).toLocaleDateString()}
              </p>
            </div>
            <div>
              {/* <p className="text-gray-700 dark:text-gray-300">
                <strong>{t('fullName')}: </strong>{report.fullName}
              </p>
              <p className="text-gray-700 dark:text-gray-300 mt-2">
                <strong>{t('email')}: </strong>{report.email}
              </p>
              <p className="text-gray-700 dark:text-gray-300 mt-2">
                <strong>{t('phoneNumber')}: </strong>{report.phoneNumber}
              </p> */}
              <p className="text-gray-700 dark:text-gray-300 mt-2">
                <strong>{t('location')}: </strong>{report.city}, {report.country}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <p className="text-gray-700 dark:text-gray-300">
              <strong>{t('description')}: </strong>{report.description}
            </p>
          </div>
        </div>

        {/* 詐騙地址（受害交易資訊） */}
        {report.scammerAddresses && report.scammerAddresses.length > 0 && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('scammerAddresses')}</h2>
            {report.scammerAddresses.map((addr, index) => (
              <div key={index} className="border-b border-gray-200 dark:border-gray-700 pb-4 mb-4">
                <p className="text-gray-700 dark:text-gray-300">
                  <strong>{t('victimWalletAddress')}: </strong>{addr.victimWalletAddress}
                </p>
                <p className="text-gray-700 dark:text-gray-300 mt-1">
                  <strong>{t('scammerWalletAddress')}: </strong>{addr.scammerWalletAddress}
                </p>
                <p className="text-gray-700 dark:text-gray-300 mt-1">
                  <strong>{t('chain')}: </strong>{addr.chain}
                </p>
                <p className="text-gray-700 dark:text-gray-300 mt-1">
                  <strong>{t('date')}: </strong>{addr.date ? new Date(addr.date).toLocaleDateString() : ''}
                </p>
                <p className="text-gray-700 dark:text-gray-300 mt-1">
                  <strong>{t('amount')}: </strong>{addr.amount} {addr.currency}
                </p>
                <p className="text-gray-700 dark:text-gray-300 mt-1">
                  <strong>{t('transactionID')}: </strong>{addr.transactionID}
                </p>
                {/* 如果有交易截圖，採用網格排列 */}
                {addr.transactionScreenshots && addr.transactionScreenshots.length > 0 && (
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {addr.transactionScreenshots.map((url, idx) => (
                      <img
                        key={idx}
                        src={url}
                        alt={`Transaction Screenshot ${idx + 1}`}
                        className="w-full h-32 object-cover rounded"
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* 網站列表 */}
        {report.websites && report.websites.length > 0 && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('websites')}</h2>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300">
              {report.websites.map((site, idx) => (
                <li key={idx}>{site.url}</li>
              ))}
            </ul>
          </div>
        )}

        {/* 代幣 ID 列表 */}
        {report.tokenIDs && report.tokenIDs.length > 0 && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('tokenIDs')}</h2>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300">
              {report.tokenIDs.map((token, idx) => (
                <li key={idx}>{token.tokenID}</li>
              ))}
            </ul>
          </div>
        )}

        {/* IP 地址 */}
        {report.ipAddresses && report.ipAddresses.length > 0 && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('ipAddresses')}</h2>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300">
              {report.ipAddresses.map((ip, idx) => (
                <li key={idx}>{ip.ip}</li>
              ))}
            </ul>
          </div>
        )}

        {/* 聯絡方式 */}
        {report.contacts && report.contacts.length > 0 && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('contacts')}</h2>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300">
              {report.contacts.map((contact, idx) => (
                <li key={idx}>{contact.contact}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default PublicReportDetails;
